import {
  customElement,
  html,
  DeesElement,
  property,
  state,
  type TemplateResult,
  cssManager,
  css,
  type CSSResult,
  unsafeCSS,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { demoFunc } from './dees-button.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-button': DeesButton;
  }
}

@customElement('dees-button')
export class DeesButton extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Button'];

  @property({
    reflect: true,
    hasChanged() {
      return true;
    }
  })
  accessor text: string = '';

  @property()
  accessor eventDetailData!: string;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor disabled = false;

  @property({
    type: Boolean
  })
  accessor isHidden = false;

  @property({
    type: String
  })
  accessor type: 'default' | 'accent' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'normal' | 'highlighted' | 'discreet' | 'big' = 'default';

  @property({
    type: String
  })
  accessor size: 'default' | 'sm' | 'lg' | 'icon' = 'default';

  @property({
    type: String
  })
  accessor status: 'normal' | 'pending' | 'success' | 'error' = 'normal';

  /**
   * Toggle state, reported to assistive technology as `aria-pressed`.
   *
   * Tri-state by design: `undefined` (the default) means this button is not a toggle at all
   * and nothing is announced, `false` a toggle that is off, `true` a toggle that is on. A
   * plain boolean defaulting to `false` would announce every button in the catalog as an
   * unpressed toggle.
   *
   * The element stays stateless — a click never flips `pressed`. The consumer owns the state
   * and sets it from its `clicked` handler.
   *
   * Only the on state reflects to the host, so `:host([pressed])` and `dees-button[pressed]`
   * select a toggle that is on. Off and not-a-toggle are indistinguishable as attributes, so
   * the attribute form (`<dees-button pressed>`) can only express "on" — any value, including
   * `pressed="false"`, means on, as for every HTML boolean attribute. The off state has to be
   * set through the property.
   */
  @property({
    type: Boolean,
    reflect: true
  })
  accessor pressed: boolean | undefined = undefined;

  /**
   * Corner treatment. 'squircle' (default) is the standard rounded rect for
   * controls that align with fields and other rectangular siblings; 'pill'
   * is the capsule for standalone/floating actions (chips, toolbar pills).
   */
  @property({
    type: String
  })
  accessor shape: 'squircle' | 'pill' = 'squircle';

  @property({
    type: Boolean,
    reflect: true
  })
  accessor insideForm: boolean = false;

  /**
   * stretches the inner button face to the host width.
   * The host is `inline-block`, so `width: 100%` on the host alone leaves the
   * `inline-flex` face shrink-wrapped. Opt in for stacked, full-bleed actions
   * (login cards, sheets, empty states).
   */
  @property({ type: Boolean, reflect: true, attribute: 'full-width' })
  accessor fullWidth: boolean = false;

  @property({ type: String, reflect: true })
  accessor icon!: string;

  @property({ type: String, reflect: true })
  accessor iconPosition: 'left' | 'right' = 'left';

  @state()
  private accessor adoptedText = '';

  @state()
  private accessor adoptedIcon = '';

  @state()
  private accessor adoptedIconPosition: 'left' | 'right' = 'left';

  private lightDomObserver: MutationObserver | undefined = undefined;

  constructor() {
    super();
  }

  public async connectedCallback(): Promise<void> {
    const superConnected = super.connectedCallback();
    this.observeLightDom();
    await superConnected;
    // Auto-detect if inside a form
    if (!this.insideForm && this.closest('dees-form')) {
      this.insideForm = true;
    }
  }

  public async disconnectedCallback(): Promise<void> {
    this.lightDomObserver?.disconnect();
    await super.disconnectedCallback();
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-block;
        box-sizing: border-box;
        font-family: inherit;
        /* status faces: solid in bright mode, tinted in dark (gobright overrides below) */
        --button-pending-bg: color-mix(in srgb, var(--dees-color-accent-primary) 20%, transparent);
        --button-pending-fg: var(--dees-color-link);
        --button-success-bg: color-mix(in srgb, var(--dees-color-accent-success) 20%, transparent);
        --button-success-fg: var(--dees-color-text-success);
        --button-error-bg: color-mix(in srgb, var(--dees-color-accent-error) 20%, transparent);
        --button-error-fg: var(--dees-color-text-error);
        /* toggle-on face: the shared selected-surface wash, an accent ring and the accent
           label colour. All three inputs are theme-aware tokens, so this face needs no
           bright/dark split of its own. */
        --button-pressed-wash: var(--dees-color-nav-selected);
        --button-pressed-ring: color-mix(in srgb, var(--dees-color-accent-primary) 50%, transparent);
        --button-pressed-fg: var(--dees-color-link);
      }
      :host([gobright]) {
        --button-pending-bg: var(--dees-color-accent-primary);
        --button-pending-fg: var(--dees-color-on-accent);
        --button-success-bg: var(--dees-color-accent-success);
        --button-success-fg: var(--dees-color-on-accent);
        --button-error-bg: var(--dees-color-accent-error);
        --button-error-fg: var(--dees-color-on-accent);
      }
      :host([hidden]) {
        display: none;
      }

      :host([full-width]) {
        display: block;
        width: 100%;
      }

      /* A full-width button is sized by its container, so its label has to yield rather
         than spill out of the face when the container is narrower than the text. It clips with
         a 1px margin rather than hiding: a button face is no scroll container, and the
         toggle-on overlay sits 1px outside the padding box so that its ring lands on the
         type's own border edge — overflow: hidden would cut exactly that pixel away. */
      :host([full-width]) .button {
        width: 100%;
        min-width: 0;
        overflow: clip;
        overflow-clip-margin: 1px;
      }

      :host([full-width]) .textbox {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Form spacing styles */
      :host([inside-form]) {
        margin-bottom: var(--dees-spacing-lg);
      }
      
      :host([inside-form]:last-child) {
        margin-bottom: 0;
      }
      
      dees-form[horizontal-layout] :host([inside-form]) {
        display: inline-block;
        margin-right: var(--dees-spacing-lg);
        margin-bottom: 0;
      }
      
      dees-form[horizontal-layout] :host([inside-form]:last-child) {
        margin-right: 0;
      }

      .button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        border-radius: var(--dees-radius-lg);
        box-sizing: border-box;
        corner-shape: var(--dees-corner-shape);
        font-weight: var(--dees-font-control-weight);
        transition: background var(--dees-transition-fast), border-color var(--dees-transition-fast),
          transform var(--dees-transition-fast) var(--dees-ease-snappy);
        cursor: pointer;
        user-select: none;
        outline: none;
        letter-spacing: -0.01em;
        gap: var(--dees-spacing-sm);
      }

      /* Shape variants. The default squircle rect aligns with fields and other
         rectangular siblings; pill is the capsule for standalone/floating
         actions. Capsules keep corner-shape round — superellipse smoothing
         would distort the semicircular end caps. */
      .button.shape-pill {
        border-radius: var(--dees-radius-full);
        corner-shape: round;
      }

      /* Size variants.
         The default sits at the standard control height (control-height-lg) so
         a button lines up with text fields, dropdowns and the segmented control
         when placed alongside them (inline controls share the same height).
         sm (control-height-sm) is the compact toolbar/table size; lg
         (control-height-xl) is the prominent CTA. */
      .button.size-default {
        height: var(--dees-control-height-lg);
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size);
      }

      .button.size-sm {
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-md);
        font-size: var(--dees-font-control-size-sm);
      }

      .button.size-lg {
        height: var(--dees-control-height-xl);
        padding: 0 var(--dees-spacing-xl);
        font-size: var(--dees-font-callout-size);
      }

      .button.size-icon {
        height: var(--dees-control-height-lg);
        width: var(--dees-control-height-lg);
        padding: 0;
      }

      /* Subtle spring press across all variants (native control feel). */
      .button:active:not(.disabled) {
        transform: scale(0.97);
      }

      /* Default variant */
      .button.default {
        background: var(--dees-color-control-bg);
        color: var(--dees-color-text-primary);
        border: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-xs);
      }

      .button.default:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
        border-color: var(--dees-color-border-strong);
      }

      .button.default:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
      }

      /* Accent variant — the filled primary action (system accent). */
      .button.accent {
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        border: 1px solid transparent;
      }
      .button.accent, .button.destructive {
        font-weight: 500;
      }

      .button.accent:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 94%, black);
      }

      .button.accent:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 85%, black);
      }

      /* Destructive variant */
      .button.destructive {
        background: var(--dees-color-action-destructive);
        color: #ffffff;
        border: 1px solid transparent;
      }

      .button.destructive:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-action-destructive) 90%, black);
      }

      .button.destructive:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-action-destructive) 82%, black);
      }

      /* Outline variant */
      .button.outline {
        background: transparent;
        color: var(--dees-color-text-primary);
        border: 1px solid var(--dees-color-border-default);
      }

      .button.outline:hover:not(.disabled) {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-strong);
      }

      .button.outline:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      /* Secondary variant */
      .button.secondary {
        background: var(--dees-color-button-secondary-bg);
        color: var(--dees-color-text-primary);
        border: 1px solid var(--dees-color-button-secondary-border);
      }

      .button.secondary:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      .button.secondary:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
      }

      /* Ghost variant */
      .button.ghost {
        background: transparent;
        color: var(--dees-color-text-primary);
        border: 1px solid transparent;
      }

      .button.ghost:hover:not(.disabled) {
        background: var(--dees-color-badge-default-bg);
      }

      .button.ghost:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      /* Link variant */
      .button.link {
        background: transparent;
        color: var(--dees-color-link);
        border: none;
        text-decoration: underline;
        text-decoration-color: transparent;
        height: auto;
        padding: 0;
      }

      .button.link:hover:not(.disabled) {
        text-decoration-color: currentColor;
      }

      /* Toggle-on face (the pressed property).
         A toggle has to stay visibly on once the pointer leaves it, so this cannot be the
         hover face; it layers on top of whatever the type paints instead of replacing it. The
         wash rides on an overlay rather than on the face's own background, because every
         type, hover and active rule sets the background shorthand and would reset a layer
         declared on the face itself. isolation scopes the overlay's negative layer to this
         face, which paints it above the type's background and below the label and icon. */
      .button.pressed {
        isolation: isolate;
      }

      .button.pressed::after {
        content: '';
        position: absolute;
        /* the face's border box, so the ring lands on the type's own border edge */
        inset: -1px;
        z-index: -1;
        border-radius: inherit;
        corner-shape: inherit;
        background: var(--button-pressed-wash);
        box-shadow: inset 0 0 0 1px var(--button-pressed-ring);
        pointer-events: none;
      }

      /* A filled face already carries the accent as its surface, where an accent wash cannot
         read, so it recesses behind a scrim with a light inner ring instead. Both faces are
         dark in either theme, which is why these two values do not follow the theme. */
      .button.accent,
      .button.destructive {
        --button-pressed-wash: color-mix(in srgb, black 24%, transparent);
        --button-pressed-ring: color-mix(in srgb, white 40%, transparent);
      }

      /* At size="icon" there is no label, so the foreground carries the state as well. Filled
         faces keep their on-accent label, and a status face keeps its own. */
      .button.pressed:not(.accent):not(.destructive):not(.pending):not(.success):not(.error) {
        color: var(--button-pressed-fg);
      }

      /* A status face owns the whole surface for as long as it shows, and type="link" has no
         surface to wash at all (no border, no padding) — its on state is the hover underline
         made permanent. aria-pressed reports the state in every one of these cases. */
      .button.pressed.pending::after,
      .button.pressed.success::after,
      .button.pressed.error::after,
      .button.pressed.link::after {
        content: none;
      }

      .button.pressed.link {
        text-decoration-color: currentColor;
      }

      /* Status states */
      .button.pending,
      .button.success,
      .button.error {
        pointer-events: none;
        padding-left: 36px; /* Space for spinner */
      }
      
      .button.size-sm.pending,
      .button.size-sm.success,
      .button.size-sm.error {
        padding-left: 32px;
      }
      
      .button.size-lg.pending,
      .button.size-lg.success,
      .button.size-lg.error {
        padding-left: 44px;
      }

      .button.pending {
        background: var(--button-pending-bg);
        color: var(--button-pending-fg);
        border: 1px solid transparent;
      }

      .button.success {
        background: var(--button-success-bg);
        color: var(--button-success-fg);
        border: 1px solid transparent;
      }

      .button.error {
        background: var(--button-error-bg);
        color: var(--button-error-fg);
        border: 1px solid transparent;
      }

      /* Disabled state */
      .button.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      /* Hidden state */
      .button.hidden {
        display: none;
      }

      /* Focus state.
         The base .button rule sets outline:none for the pointer path; the ring is restored
         here for :focus-visible only, so a mouse press never leaves a ring behind while
         keyboard focus is always visible. This rule is the focus affordance keyboard users
         actually see — do not remove it alongside the base outline:none. */
      .button:focus-visible {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 2px;
        /* The indicator has to appear at once. The base .button rule carries
           transition:all, which animates outline-color up from its initial currentColor —
           measured as ~50ms of a near-white ring on a bright accent face, i.e. briefly
           invisible right when the user needs it. Only the focused state opts out. */
        transition: none;
      }

      /* A disabled face is removed from the tab order, so it should never show a ring. */
      .button.disabled:focus-visible {
        outline: none;
      }

      @media (prefers-reduced-motion: reduce) {
        .button { transition: none; }
        .button:active:not(.disabled) { transform: none; }
      }

      /* Loading spinner */
      dees-spinner {
        position: absolute;
        left: 10px;
        width: 16px;
        height: 16px;
      }
      
      .button.size-sm dees-spinner {
        left: 8px;
        width: 14px;
        height: 14px;
      }
      
      .button.size-lg dees-spinner {
        left: 14px;
        width: 18px;
        height: 18px;
      }
      
      /* Icon sizing within buttons */
      .button dees-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }
      
      .button.size-sm dees-icon {
        width: 14px;
        height: 14px;
      }
      
      .button.size-lg dees-icon {
        width: 18px;
        height: 18px;
      }

      /* Text alignment */
      .textbox {
        display: flex;
        align-items: center;
      }

    `,
  ];

  private observeLightDom(): void {
    this.lightDomObserver ??= new MutationObserver((mutationsArg) => {
      const supportedContentChanged = mutationsArg.some((mutationArg) => {
        if (mutationArg.type === 'childList') {
          return mutationArg.target === this;
        }
        if (mutationArg.type === 'characterData') {
          return mutationArg.target.parentNode === this;
        }
        return mutationArg.target.parentNode === this
          && (mutationArg.target as Element).localName === 'dees-icon';
      });
      if (supportedContentChanged) {
        this.adoptLightDom();
      }
    });
    this.lightDomObserver.observe(this, {
      attributes: true,
      attributeFilter: ['icon'],
      characterData: true,
      childList: true,
      subtree: true,
    });
    this.adoptLightDom();
  }

  /** Mirrors supported direct light content without taking ownership of consumer nodes. */
  private adoptLightDom(): void {
    const children = Array.from(this.childNodes);
    const textNodes = children.filter(
      (nodeArg) => nodeArg.nodeType === Node.TEXT_NODE && nodeArg.textContent?.trim(),
    );
    const iconElement = children.find(
      (nodeArg): nodeArg is HTMLElement & { icon?: string } =>
        nodeArg instanceof HTMLElement && nodeArg.localName === 'dees-icon',
    );

    const nextAdoptedText = textNodes
      .map((nodeArg) => nodeArg.textContent!.trim())
      .join(' ');
    const nextAdoptedIcon = iconElement?.icon || iconElement?.getAttribute('icon') || '';
    const nextAdoptedIconPosition =
      iconElement && textNodes.length > 0
        ? children.indexOf(iconElement) < children.indexOf(textNodes[0])
          ? 'left'
          : 'right'
        : 'left';

    const textFollowsLightDom = this.text === this.adoptedText;
    const iconFollowsLightDom = (this.icon || '') === this.adoptedIcon;
    const iconPositionFollowsLightDom = this.iconPosition === this.adoptedIconPosition;
    const previousAdoptedIcon = this.adoptedIcon;

    this.adoptedText = nextAdoptedText;
    this.adoptedIcon = nextAdoptedIcon;
    this.adoptedIconPosition = nextAdoptedIconPosition;

    if (textFollowsLightDom && this.text !== nextAdoptedText) {
      this.text = nextAdoptedText;
    }
    if (
      iconFollowsLightDom
      && this.icon !== nextAdoptedIcon
      && Boolean(nextAdoptedIcon || previousAdoptedIcon)
    ) {
      this.icon = nextAdoptedIcon;
    }
    if (iconPositionFollowsLightDom && this.iconPosition !== nextAdoptedIconPosition) {
      this.iconPosition = nextAdoptedIconPosition;
    }
  }

  public render(): TemplateResult {
    // Map old types to new types for backward compatibility
    const typeMap: {[key: string]: string} = {
      'normal': 'default',
      'highlighted': 'accent',
      'discreet': 'outline',
      'big': 'default' // Will use size instead
    };

    const actualType = typeMap[this.type] || this.type;
    const actualSize = this.type === 'big' ? 'lg' : this.size;
    const effectiveText = this.text;
    const effectiveIcon = this.icon;
    const effectiveIconPosition = this.iconPosition;

    const leftIcon = effectiveIconPosition === 'left' && effectiveIcon
      ? html`<dees-icon .icon=${effectiveIcon}></dees-icon>`
      : '';
    const rightIcon = effectiveIconPosition === 'right' && effectiveIcon
      ? html`<dees-icon .icon=${effectiveIcon}></dees-icon>`
      : '';

    // No placeholder label: a button without text renders icon-only.
    // size-icon additionally suppresses text to keep its square geometry.
    const showText = Boolean(effectiveText) && !(actualSize === 'icon' && effectiveIcon);

    // A button that is not a toggle must carry no `aria-pressed` attribute at all, and an
    // attribute binding cannot remove one — lit only removes on its `nothing` sentinel, which
    // `@design.estate/dees-element` does not re-export. The state therefore goes through the
    // reflected ARIA property, the same channel `.ariaLabel` uses below: `null` removes it.
    return html`
      <div
        part="button"
        class="button ${this.isHidden ? 'hidden' : ''} ${actualType} size-${actualSize} shape-${this.shape} ${this.status} ${this.pressed
          ? 'pressed'
          : ''} ${this.disabled
          ? 'disabled'
          : ''}"
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-busy=${this.status === 'pending' ? 'true' : 'false'}
        .ariaLabel=${showText ? null : effectiveText || null}
        .ariaPressed=${this.pressed === undefined ? null : String(this.pressed)}
        @click="${this.dispatchClick}"
        @keydown=${this.handleKeydown}
        @keyup=${this.handleKeyup}
      >
        ${this.status === 'normal' ? html``: html`
          <dees-spinner
            .bnw=${true}
            status="${this.status}"
            size="${actualSize === 'sm' ? 14 : actualSize === 'lg' ? 18 : 16}"
          ></dees-spinner>
        `}
        ${leftIcon}
        ${showText ? html`<div class="textbox">${effectiveText}</div>` : ''}
        ${rightIcon}
      </div>
    `;
  }

  public async dispatchClick() {
    if (this.isInert) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('clicked', {
        detail: {
          data: this.eventDetailData,
        },
        bubbles: true,
      })
    );
  }

  /** the inner face — the element that actually carries focus */
  private get faceElement(): HTMLElement | null {
    return (this.shadowRoot?.querySelector('.button') as HTMLElement | null) || null;
  }

  /**
   * States in which the button must not react to input. `disabled` is obvious; the status
   * faces matter too, because `.button.pending / .success / .error` already set
   * `pointer-events: none` and keyboard activation has to refuse the same interactions a
   * mouse cannot make.
   */
  private get isInert(): boolean {
    return this.disabled || this.status !== 'normal';
  }

  /**
   * Focus lives on the inner face inside the shadow root, so `host.focus()` has to forward
   * or it is a silent no-op. Form submit wrappers forward focus here as well.
   */
  public focus(optionsArg?: FocusOptions): void {
    const face = this.faceElement;
    if (face) {
      face.focus(optionsArg);
      return;
    }
    super.focus(optionsArg);
  }

  public blur(): void {
    this.faceElement?.blur();
    super.blur();
  }

  private handleKeydown = (eventArg: KeyboardEvent): void => {
    if (eventArg.key === ' ' || eventArg.key === 'Spacebar') {
      // Native buttons activate Space on keyup. Swallow the keydown so the page cannot
      // scroll underneath the focused button.
      eventArg.preventDefault();
      return;
    }
    if (eventArg.key === 'Enter') {
      eventArg.preventDefault();
      this.activateFromKeyboard();
    }
  };

  private handleKeyup = (eventArg: KeyboardEvent): void => {
    if (eventArg.key === ' ' || eventArg.key === 'Spacebar') {
      eventArg.preventDefault();
      this.activateFromKeyboard();
    }
  };

  /**
   * Keyboard activation goes through a real click rather than dispatching `clicked`
   * directly, exactly as a native button does. Several catalog components bind `@click` on
   * dees-button instead of `@clicked` (dees-workspace-diff-editor, the profile picture
   * modal), and both listener styles have to keep working from the keyboard.
   *
   * `pointer-events: none` does not block a programmatic `click()`, so inertness is
   * enforced here rather than left to CSS.
   */
  private activateFromKeyboard(): void {
    if (this.isInert) {
      return;
    }
    this.faceElement?.click();
  }
}
